import * as Sentry from '@sentry/react';
import {
  AlertsDashboard,
  AllowedStatesDashboard,
  BoardBuilderDashboard,
  CorrelationsDashboard,
  GameModesDashboard,
  GameTypesDashboard,
  LeaguesDashboard,
  MemberGroupsDashboard,
  MembersDashboard,
  MissionsDashboard,
  PaymentsDashboard,
  PlayerPacksDashboard,
  PlayersDashboard,
  PollsDashboard,
  PromotionsDashboard,
  ResolutionsDashboard,
  RolesDashboard,
  RulesetsDashboard,
  ScoringDashboard,
  SimulationsDashboard,
  StatTypeDashboard,
  StreakDashboard,
  TeamsDashboard,
  TradingDashboard,
  UnsettlementsDashboard,
  UserRolesDashboard
} from '@bo-views/index';
import type { CustomRoute, GroupedRoutes } from '@bo-types/Router';
import {
  DASHBOARD_ACCESS_PERMISSIONS,
  MEMBER_GROUP_PERMISSIONS,
  MEMBER_TAB_VIEW_PERMISSIONS,
  MISSION_PERMISSIONS,
  PROMOTIONS_PERMISSIONS
} from '@bo-constants/permissions';
import {
  MEMBER_DETAILS_CHILD_PATHS,
  MEMBER_DETAILS_PATH,
  MEMBER_SUPPORT_PATH,
  MISSIONS_CHILD_PATHS,
  PATHS,
  PLAYER_PACKS_CHILD_PATHS,
  PROMOTIONS_CHILD_PATHS,
  RESOLUTION_DETAILS_PATH,
  SIMULATION_DETAILS_PATH,
  SIMULATION_EXISTING_PATH,
  UNSETTLEMENTS_CHILD_PATHS
} from '@bo-constants/routes';
import {
  PlayerPackCreate,
  PlayerPackDetails,
  PlayerPacksList
} from '@bo-components/playerPacks';
import {
  Route,
  createBrowserRouter,
  createRoutesFromElements
} from 'react-router-dom';
import {
  SimulationDetails,
  SimulationList,
  SimulationsHomePage
} from '@bo-components/simulations';
import {
  UnsettlementsCreate,
  UnsettlementsList
} from '@bo-components/unsettlements';
import { AuthenticatedRoutes } from '@bo-components/navigation/AuthenticatedRoutes';
import { Authentication } from '@bo-components/navigation/Authentication';
import { CreateMission } from '@bo-components/missions/CreateMission';
import { CreatePromotionEntry } from '@bo-components/promotions/CreatePromotionEntry';
import { MemberDeposits } from '@bo-components/members/memberDeposits';
import { MemberDetails } from '@bo-components/members';
import { MemberEntries } from '@bo-components/members/entries';
import { MemberNotes } from '@bo-components/members/memberNotes';
import { MemberPolls } from '@bo-components/members/memberPolls';
import { MemberProfile } from '@bo-components/members/memberProfile';
import { MemberPromotions } from '@bo-components/members/memberPromotions';
import { MemberReferrals } from '@bo-components/members/memberReferrals';
import { MemberResponsibleGaming } from '@bo-components/members/memberResponsibleGaming';
import { MemberSupport } from '@bo-components/members/memberSupport';
import { MemberTransactions } from '@bo-components/members/memberTransactions';
import { MemberWithdrawals } from '@bo-components/members/memberWithdrawals';
import { Members } from '@bo-components/members/membersList';
import { Missions } from '@bo-components/missions/Missions';
import { PermissionDenied } from '@bo-components/navigation/PermissionDenied';
import { PromotionApprovalsDashboard } from '@bo-components/promotions/approvals/PromotionApprovalsDashboard';
import { Promotions } from '@bo-components/promotions/Promotions';
import { ResolutionDetails } from '@bo-components/resolutions/resolutionDetails';
import { ResolutionsList } from '@bo-components/resolutions/resolutionsList';
import { SignIn } from '@bo-components/sign-in/SignIn';

import { ViewMission } from '@bo-components/missions/ViewMission';
import { getActiveRoutes } from '@bo-utils/routes';

const {
  deposits,
  entries,
  notes,
  polls,
  profile,
  promotions,
  referrals,
  responsibleGaming,
  transactions,
  withdrawals
} = MEMBER_DETAILS_CHILD_PATHS;

const membersRoute: CustomRoute = {
  component: <Members />,
  isIndexRoute: true,
  title: 'MembersList'
};

const memberSupportRoute: CustomRoute = {
  component: <MemberSupport />,
  path: MEMBER_SUPPORT_PATH,
  title: 'MemberSupport'
};

const {
  MEMBER_VIEW_ENTRIES,
  MEMBER_VIEW_NOTES,
  MEMBER_VIEW_PAYMENTS,
  MEMBER_VIEW_POLLS,
  MEMBER_VIEW_PROMOTIONS,
  MEMBER_VIEW_REFERRALS,
  MEMBER_VIEW_RG,
  MEMBER_VIEW_TRANSACTIONS
} = MEMBER_TAB_VIEW_PERMISSIONS;

const { MEMBER_GROUPS_VIEW } = MEMBER_GROUP_PERMISSIONS;

const { MISSION_CREATE, MISSION_VIEW } = MISSION_PERMISSIONS;

const { PROMOTIONS_CREATE } = PROMOTIONS_PERMISSIONS;

/* Do not change the sequence of this array items, the tabs on the member details page are displayed in this sequence */
export const memberDetailsRoute: CustomRoute = {
  component: <MemberDetails />,
  childRoutes: [
    {
      component: <MemberProfile />,
      isIndexRoute: true,
      path: profile,
      title: 'Profile'
    },
    {
      component: <MemberEntries />,
      path: entries,
      permissionName: MEMBER_VIEW_ENTRIES,
      title: 'Entries'
    },
    {
      component: <MemberTransactions />,
      path: transactions,
      permissionName: MEMBER_VIEW_TRANSACTIONS,
      title: 'Transactions'
    },
    {
      component: <MemberDeposits />,
      path: deposits,
      permissionName: MEMBER_VIEW_PAYMENTS,
      title: 'Deposits'
    },
    {
      component: <MemberWithdrawals />,
      path: withdrawals,
      permissionName: MEMBER_VIEW_PAYMENTS,
      title: 'Withdrawals'
    },
    {
      component: <MemberReferrals />,
      path: referrals,
      permissionName: MEMBER_VIEW_REFERRALS,
      title: 'Referrals'
    },
    {
      component: <MemberResponsibleGaming />,
      path: responsibleGaming,
      permissionName: MEMBER_VIEW_RG,
      title: 'Responsible Gaming'
    },
    {
      component: <MemberPromotions />,
      path: promotions,
      permissionName: MEMBER_VIEW_PROMOTIONS,
      title: 'Promotions'
    },
    {
      component: <MemberNotes />,
      path: notes,
      permissionName: MEMBER_VIEW_NOTES,
      title: 'Notes'
    },
    {
      component: <MemberPolls />,
      path: polls,
      permissionName: MEMBER_VIEW_POLLS,
      title: 'Polls'
    }
  ],
  path: MEMBER_DETAILS_PATH,
  title: 'MemberDetails'
};

const simulationChildRoutes = [
  {
    component: <SimulationsHomePage />,
    isIndexRoute: true,
    title: 'Simulations'
  },
  {
    component: <SimulationList />,
    path: SIMULATION_EXISTING_PATH,
    title: 'Simulation List'
  },
  {
    component: <SimulationDetails />,
    path: SIMULATION_DETAILS_PATH,
    title: 'Simulation Details'
  }
];

const promotionsChildRoutes = [
  {
    component: <Promotions />,
    isIndexRoute: true,
    title: 'Promotions'
  },
  {
    component: <CreatePromotionEntry />,
    path: PROMOTIONS_CHILD_PATHS.create,
    permissionName: PROMOTIONS_CREATE,
    title: 'Create Promotion'
  }
];

const missionsChildRoutes = [
  {
    component: <Missions />,
    isIndexRoute: true,
    title: 'Missions'
  },
  {
    component: <CreateMission />,
    path: MISSIONS_CHILD_PATHS.create,
    permissionName: MISSION_CREATE,
    title: 'Create Mission'
  },
  {
    component: <ViewMission />,
    path: MISSIONS_CHILD_PATHS.view,
    permissionName: MISSION_VIEW,
    title: 'Mission'
  }
];

const playerPacksChildRoutes = [
  {
    component: <PlayerPacksList />,
    isIndexRoute: true,
    title: 'Player Packs List'
  },
  {
    component: <PlayerPackDetails />,
    path: PLAYER_PACKS_CHILD_PATHS.view,
    title: 'Player Packs Details'
  },
  {
    component: <PlayerPackCreate />,
    path: PLAYER_PACKS_CHILD_PATHS.create,
    title: 'Player Packs Create'
  }
];

const resolutionChildRoutes = [
  {
    component: <ResolutionsList />,
    isIndexRoute: true,
    title: 'Resolutions List'
  },
  {
    component: <ResolutionDetails />,
    path: RESOLUTION_DETAILS_PATH,
    title: 'Resolution Details'
  }
];
const unsettlementsChildRoutes = [
  {
    component: <UnsettlementsList />,
    isIndexRoute: true,
    title: 'Unsettlements List'
  },
  {
    component: <UnsettlementsCreate />,
    path: UNSETTLEMENTS_CHILD_PATHS.create,
    title: 'Unsettlements Create'
  }
];

const {
  ALERTS,
  ADMIN_USERS,
  BOARD_BUILDER,
  CORRELATIONS,
  // LEAGUES,
  MEMBERS,
  PAYMENTS,
  PLAYER_PACKS,
  PLAYERS,
  POLLS,
  PROMOTIONS,
  PROMOTION_APPROVALS,
  MISSIONS,
  RESOLUTIONS,
  ROLES,
  RULESETS,
  SCORING,
  SIMULATIONS,
  STAT_TYPE,
  ALLOWED_STATES,
  STREAK,
  TRADING,
  GAME_TYPES,
  GAME_MODE,
  UNSETTLEMENTS,
  TEAMS
} = DASHBOARD_ACCESS_PERMISSIONS;

export const ROUTES: Array<GroupedRoutes> = [
  {
    groupName: 'Game Experience',
    routes: [
      {
        childRoutes: simulationChildRoutes,
        component: <SimulationsDashboard />,
        featureFlagName: 'SIMULATIONS',
        isFeatureFlagControlled: true,
        path: PATHS.SIMULATIONS,
        permissionName: SIMULATIONS,
        title: 'Simulations'
      },
      {
        component: <BoardBuilderDashboard />,
        featureFlagName: 'BOARD_BUILDER',
        isFeatureFlagControlled: true,
        path: PATHS.BOARD_BUILDER,
        permissionName: BOARD_BUILDER,
        title: 'Board Builder'
      },
      {
        component: <TradingDashboard />,
        featureFlagName: 'TRADING',
        isFeatureFlagControlled: true,
        path: PATHS.TRADING,
        permissionName: TRADING,
        title: 'Trading'
      },
      {
        component: <ScoringDashboard />,
        featureFlagName: 'SCORING',
        isFeatureFlagControlled: true,
        path: PATHS.SCORING,
        permissionName: SCORING,
        title: 'Scoring'
      },
      {
        component: <CorrelationsDashboard />,
        featureFlagName: 'CORRELATIONS',
        isFeatureFlagControlled: true,
        path: PATHS.CORRELATIONS,
        permissionName: CORRELATIONS,
        title: 'Correlations'
      }
    ]
  },
  {
    groupName: 'Entities',
    routes: [
      {
        component: <AllowedStatesDashboard />,
        featureFlagName: 'ALLOWED_STATES',
        isFeatureFlagControlled: false,
        path: PATHS.ALLOWED_STATES,
        permissionName: ALLOWED_STATES,
        title: 'Allowed States'
      },
      {
        component: <GameModesDashboard />,
        featureFlagName: 'GAME_MODES',
        isFeatureFlagControlled: true,
        path: PATHS.GAME_MODES,
        permissionName: GAME_MODE,
        title: 'Game Modes'
      },
      {
        component: <GameTypesDashboard />,
        featureFlagName: 'GAME_TYPES',
        isFeatureFlagControlled: true,
        path: PATHS.GAME_TYPES,
        permissionName: GAME_TYPES,
        title: 'Game Types'
      },
      {
        component: <LeaguesDashboard />,
        featureFlagName: 'LEAGUES',
        isFeatureFlagControlled: false,
        permissionName: STAT_TYPE,
        path: PATHS.LEAGUES,
        title: 'Leagues'
      },
      {
        childRoutes: playerPacksChildRoutes,
        component: <PlayerPacksDashboard />,
        featureFlagName: 'PLAYER_PACKS',
        isFeatureFlagControlled: true,
        path: PATHS.PLAYER_PACKS,
        permissionName: PLAYER_PACKS,
        title: 'Player Packs'
      },
      {
        component: <PlayersDashboard />,
        featureFlagName: 'PLAYERS',
        isFeatureFlagControlled: true,
        path: PATHS.PLAYERS,
        permissionName: PLAYERS,
        title: 'Players'
      },
      {
        component: <StatTypeDashboard />,
        featureFlagName: 'STAT_TYPE',
        isFeatureFlagControlled: true,
        path: PATHS.STAT_TYPE,
        permissionName: STAT_TYPE,
        title: 'Stat Type'
      },
      {
        component: <TeamsDashboard />,
        featureFlagName: 'TEAMS',
        isFeatureFlagControlled: true,
        path: PATHS.TEAMS,
        permissionName: TEAMS,
        title: 'Teams'
      }
    ]
  },
  {
    groupName: 'Account Settings',
    routes: [
      {
        component: <PaymentsDashboard />,
        featureFlagName: 'PAYMENTS',
        isFeatureFlagControlled: true,
        path: PATHS.PAYMENTS,
        permissionName: PAYMENTS,
        title: 'Payments'
      },
      {
        childRoutes: resolutionChildRoutes,
        component: <ResolutionsDashboard />,
        featureFlagName: 'RESOLUTIONS',
        isFeatureFlagControlled: true,
        path: PATHS.RESOLUTIONS,
        permissionName: RESOLUTIONS,
        title: 'Resolutions'
      },
      {
        childRoutes: unsettlementsChildRoutes,
        component: <UnsettlementsDashboard />,
        featureFlagName: 'UNSETTLEMENTS',
        isFeatureFlagControlled: true,
        path: PATHS.UNSETTLEMENTS,
        permissionName: UNSETTLEMENTS,
        title: 'Unsettlements'
      }
    ]
  },
  {
    groupName: 'User Management',
    routes: [
      {
        component: <UserRolesDashboard />,
        featureFlagName: 'ADMIN_USERS',
        isFeatureFlagControlled: true,
        path: PATHS.ADMIN_USERS,
        permissionName: ADMIN_USERS,
        title: 'Admin Users'
      },
      {
        component: <RolesDashboard />,
        featureFlagName: 'ROLES',
        isFeatureFlagControlled: true,
        path: PATHS.ROLES,
        permissionName: ROLES,
        title: 'Roles'
      },
      {
        childRoutes: [membersRoute, memberDetailsRoute, memberSupportRoute],
        component: <MembersDashboard />,
        featureFlagName: 'MEMBERS',
        isFeatureFlagControlled: true,
        path: PATHS.MEMBERS,
        permissionName: MEMBERS,
        title: 'Members'
      },
      {
        component: <MemberGroupsDashboard />,
        featureFlagName: 'MEMBER_GROUPS',
        isFeatureFlagControlled: true,
        path: PATHS.MEMBER_GROUPS,
        permissionName: MEMBER_GROUPS_VIEW,
        title: 'Member Groups'
      }
    ]
  },
  {
    groupName: 'Rulesets',
    routes: [
      {
        component: <RulesetsDashboard />,
        featureFlagName: 'RULESETS',
        isFeatureFlagControlled: true,
        path: PATHS.RULESETS,
        permissionName: RULESETS,
        title: 'Rulesets'
      }
    ]
  },
  {
    groupName: 'Other Game Types',
    routes: [
      {
        component: <StreakDashboard />,
        featureFlagName: 'STREAK',
        isFeatureFlagControlled: true,
        path: PATHS.STREAK,
        permissionName: STREAK,
        title: 'Streak Board'
      },
      {
        component: <PollsDashboard />,
        featureFlagName: 'POLLS',
        isFeatureFlagControlled: true,
        path: PATHS.POLLS,
        permissionName: POLLS,
        title: 'Polls Board'
      }
    ]
  },
  {
    groupName: 'Engagement',
    routes: [
      {
        component: <PromotionsDashboard />,
        featureFlagName: 'PROMOTIONS',
        isFeatureFlagControlled: true,
        path: PATHS.PROMOTIONS,
        permissionName: PROMOTIONS,
        title: 'Promotions',
        childRoutes: promotionsChildRoutes
      },
      {
        component: <PromotionApprovalsDashboard />,
        featureFlagName: 'PROMOTION_APPROVALS',
        isFeatureFlagControlled: true,
        path: PATHS.PROMOTION_APPROVALS,
        permissionName: PROMOTION_APPROVALS,
        title: 'Approvals'
      },
      {
        component: <MissionsDashboard />,
        featureFlagName: 'MISSIONS',
        isFeatureFlagControlled: true,
        path: PATHS.MISSIONS,
        permissionName: MISSIONS,
        title: 'Missions',
        childRoutes: missionsChildRoutes
      }
    ]
  },
  {
    groupName: 'Alerts',
    routes: [
      {
        component: <AlertsDashboard />,
        featureFlagName: 'ALERTS',
        isFeatureFlagControlled: true,
        path: PATHS.ALERTS,
        permissionName: ALERTS,
        title: 'Alerts'
      }
    ]
  }
];

export const routes = ({ availableRoutes }: { availableRoutes?: string }) => {
  const activeRouterRoutes = getActiveRoutes({ availableRoutes });

  const generateCustomRoute = ({ route }: { route: CustomRoute }) => {
    const { component, childRoutes, isIndexRoute = false, path, title } = route;
    const key = path || title;

    return childRoutes && childRoutes.length > 0 && !isIndexRoute ? (
      <Route element={component} key={key} path={path}>
        {childRoutes?.map((childRoute) =>
          generateCustomRoute({ route: childRoute })
        )}
      </Route>
    ) : (
      <Route index={isIndexRoute} element={component} key={key} path={path} />
    );
  };

  return (
    <Route path="/" element={<Authentication />}>
      <Route index element={<SignIn />} />
      <Route path="" element={<AuthenticatedRoutes />}>
        {activeRouterRoutes.map((route) => generateCustomRoute({ route }))}
      </Route>
      <Route path="*" element={<PermissionDenied />} />
    </Route>
  );
};

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

export const router = ({ availableRoutes }: { availableRoutes?: string }) =>
  sentryCreateBrowserRouter(
    createRoutesFromElements(<>{routes({ availableRoutes })}</>)
  );
